import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Avatar,
    Typography,
    Chip,
    IconButton,
    Tooltip,
    Box,
    useTheme,
    alpha,
    TablePagination,
    CircularProgress
} from '@mui/material';
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    AdminPanelSettings as AdminIcon,
    School as SchoolIcon,
    Groups as TeacherIcon
} from '@mui/icons-material';
import { User } from '../../../types/auth';

interface UserTableProps {
    users: User[];
    loading: boolean;
    page: number;
    rowsPerPage: number;
    totalUsers: number;
    onPageChange: (event: unknown, newPage: number) => void;
    onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onEdit: (user: User) => void;
    onDelete: (user: User) => void;
}

const UserTable: React.FC<UserTableProps> = ({
    users,
    loading,
    page,
    rowsPerPage,
    totalUsers,
    onPageChange,
    onRowsPerPageChange,
    onEdit,
    onDelete
}) => {
    const theme = useTheme();

    const getRoleColor = (role: string) => {
        switch (role) {
            case 'admin':
                return theme.palette.primary.main;
            case 'teacher':
                return theme.palette.success.main;
            case 'student':
                return theme.palette.info.main;
            default:
                return theme.palette.text.secondary;
        }
    };

    const getRoleIcon = (role: string) => {
        switch (role) {
            case 'admin':
                return <AdminIcon fontSize="small" />;
            case 'teacher':
                return <TeacherIcon fontSize="small" />;
            case 'student':
                return <SchoolIcon fontSize="small" />;
            default:
                return <SchoolIcon fontSize="small" />;
        }
    };

    const stringAvatar = (name: string) => {
        const nameParts = name.split(' ');
        return {
            children: `${nameParts[0][0]}${nameParts[1] ? nameParts[1][0] : ''}`,
        };
    };

    return (
        <Paper
            elevation={0}
            sx={{
                width: '100%',
                overflow: 'hidden',
                borderRadius: 4,
                border: `1px solid ${theme.palette.divider}`,
                boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.05)}`
            }}
        >
            <TableContainer sx={{ maxHeight: 640 }}>
                <Table stickyHeader aria-label="sticky table">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 'bold', bgcolor: alpha(theme.palette.primary.main, 0.04) }}>User</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', bgcolor: alpha(theme.palette.primary.main, 0.04) }}>Role</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', bgcolor: alpha(theme.palette.primary.main, 0.04) }}>School</TableCell>
                            <TableCell sx={{ fontWeight: 'bold', bgcolor: alpha(theme.palette.primary.main, 0.04) }}>Joined Date</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: alpha(theme.palette.primary.main, 0.04) }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : users.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 8 }}>
                                    <Typography color="text.secondary">No users found</Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            users.map((user) => {
                                const fullName = `${user.firstName} ${user.lastName}`;
                                return (
                                    <TableRow hover role="checkbox" tabIndex={-1} key={user.id} sx={{ '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.02) } }}>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Avatar
                                                    {...stringAvatar(fullName)}
                                                    sx={{
                                                        bgcolor: user.profile?.profileColor || theme.palette.primary.main,
                                                        width: 40,
                                                        height: 40,
                                                        fontSize: '0.875rem',
                                                        fontWeight: 'bold'
                                                    }}
                                                />
                                                <Box>
                                                    <Typography variant="subtitle2" fontWeight="600">
                                                        {fullName}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {user.email}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={getRoleIcon(user.role)}
                                                label={user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                                                size="small"
                                                sx={{
                                                    bgcolor: alpha(getRoleColor(user.role), 0.1),
                                                    color: getRoleColor(user.role),
                                                    fontWeight: '600',
                                                    border: `1px solid ${alpha(getRoleColor(user.role), 0.2)}`
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {user.profile?.school || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {new Date(user.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                                <Tooltip title="Edit User">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => onEdit(user)}
                                                        sx={{
                                                            color: theme.palette.text.secondary,
                                                            '&:hover': { color: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.1) }
                                                        }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete User">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => onDelete(user)}
                                                        sx={{
                                                            color: theme.palette.text.secondary,
                                                            '&:hover': { color: theme.palette.error.main, bgcolor: alpha(theme.palette.error.main, 0.1) }
                                                        }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[10, 25, 100]}
                component="div"
                count={totalUsers}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
            />
        </Paper>
    );
};

export default UserTable;
